import type { ApiFieldDef, FieldDataType, ProfileFieldEntry } from '@/types/inventory'

export interface ResolvedProfileField {
  key: string
  label: string
  dataType: FieldDataType
  active: boolean
  value: unknown
}

/**
 * Resolve a profile-type's field definitions against a stored fields map into an
 * ordered, display-ready list. One row per def, ordered by def `order` ascending.
 * A def with no entry in `fields` falls back to its `activeByDefault` and an
 * undefined value. Pure — no React/fetch.
 */
export function resolveProfileFields(
  defs: ApiFieldDef[],
  fields: Record<string, ProfileFieldEntry>,
): ResolvedProfileField[] {
  return [...defs]
    .sort((a, b) => a.order - b.order)
    .map((def) => {
      const entry = fields[def.key]
      return {
        key: def.key,
        label: def.label,
        dataType: def.dataType,
        active: entry ? entry.active : def.activeByDefault,
        value: entry ? entry.value : undefined,
      }
    })
}

/**
 * Whether this field hands a value down to everything assigned to the profile.
 *
 * A boolean is excluded on purpose: its switch IS its value, so every enabled
 * boolean would count as "has a default" and the number would say nothing.
 */
export function hasDefault(field: ResolvedProfileField): boolean {
  if (field.dataType === 'boolean') return false
  return field.value !== null && field.value !== undefined && field.value !== ''
}

/** Format a field value for display. Booleans → Yes/No; null/undefined → em dash. */
export function formatFieldValue(dataType: FieldDataType, value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (dataType === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}
